import { Component, OnDestroy, OnInit, Output, EventEmitter, Input, ViewChild } from '@angular/core';
import { Events } from '../../helpers/events';

// ionic
import { ModalController, PopoverController } from '@ionic/angular';
// import { PopoverController, Platform } from '@ionic/angular';

// other libraries
import { TranslateService, LangChangeEvent } from "@ngx-translate/core";

// rxjs
import { Subject, Subscription } from "rxjs";
import { debounceTime, take, takeUntil } from "rxjs/operators";

// components
import { MarketplaceAddComponent } from "../marketplace-add/marketplace-add.component";
import { MarketplaceKeywordsComponent } from "../marketplace-keywords/marketplace-keywords.component";
import { MarketplaceInfoComponent } from "../marketplace-info/marketplace-info.component";

// providers
import { MarketplaceService } from "../../services/marketplace.service";
import { UserService } from "../../services/user.service";
import { EventService } from "../../services/event.service";
import { OverlayService } from "../../services/overlay.service";
import { PlatformService } from "../../services/platform.service";
import { PaginatorService } from "../../services/paginator.service";
import { RouterExtService } from "../../services/router-ext.service";

// models
import { Marketplace } from "../../models/marketplace";
import { MarketplacePost } from "../../models/marketplace-post";
import { User } from "../../models/user";
import { Event } from "../../models/event";
import { SelectBoxOption } from "../../models/select-box-option";

@Component({
    selector: 'marketplace-list',
    templateUrl: './marketplace-list.component.html',
    styleUrls: ['./marketplace-list.component.scss'],
})
export class MarketplaceListComponent implements OnInit, OnDestroy {

    /**
     * marketplace keywords
     *
     * @type ViewChild
     */
    @ViewChild(MarketplaceKeywordsComponent) keywords: MarketplaceKeywordsComponent;

    /**
     * selected event
     *
     * @type {Event}
     */
    @Input() event: Event;

    /**
     * selected marketplace
     *
     * @type {Marketplace}
     */
    @Input() marketplace: Marketplace;

    @Output() onMarketplacePostEdit: EventEmitter<MarketplacePost> = new EventEmitter();

    /**
     * unsubscribe subject
     *
     * @type {Subject<void>}
     */
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    /**
     * subscription for event changes subject
     *
     * @type {Subscription}
     */
    private subscriptionEvent: Subscription;

    /**
     * current event
     *
     * @type {User}
     */
    public user: User;

    /**
     * selected language
     *
     * @type string
     */
    public userLang: string;

    /**
     * initial loading state
     *
     * @type {boolean}
     */
    public loading: boolean = false;

    /**
     * initial loading state
     *
     * @type {boolean}
     */
    public dataLoading: boolean = false;

    /**
     * list filter
     *
     * @type string
     */
    public categoryId: number = 0;
    public filterKeywords: any[] = null;
    public filterOptions: SelectBoxOption[] = [];

    public editId: number;

    /**
     * search string
     *
     * @type {string}
     */
    public search: string;

    /**
     * Marketplace post
     *
     * @type {MarketplacePost}
     */
    public items: MarketplacePost[] = [];

    itemsTotal: number = 0;
    itemsLimit: number = 5;
    itemsOffset: number = 0;

    // handlers for events
    keywordsChangeHandler;
    eventChangeHandler;
    refreshHandler;
    postUpdated;

    /**
     * which select type should be used.. alert/popover/action-sheet
     *
     * @type {string}
     */
    public selectType: string = 'popover';

    /**
     * show loading in chat box for infinite loading
     *
     * @type {boolean}
     */
    public loadingInfinite: boolean = false;

    /**
     * constructor
     *
     * @param eventService
     * @param appEvents
     */
    constructor(
        public userService: UserService,
        public eventService: EventService,
        public translate: TranslateService,
        public appEvents: Events,
        public plt: PlatformService,
        public overlayService: OverlayService,
        public paginatorService: PaginatorService,
        public marketplaceService: MarketplaceService,
        public modalController: ModalController,
        public routerExtService: RouterExtService
    ) {
        this.userLang = this.translate.currentLang;

        if (this.plt.sizeSm) {
            this.selectType = 'action-sheet';
        }

        this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
            this.userLang = event.lang;
        });
    }

    /**
     * on init
     *
     * @return void
     */
    ngOnInit() {
        // watch event change
        this.eventChangeHandler = () => {
            this.itemsOffset = 0;
        }

        this.refreshHandler = () => {
            this.refresh(null, true, false);
        }

        // watch marketplace keywords changes
        this.keywordsChangeHandler = () => {
            this.applyFilter();
        }

        // handler for procesing updates of specific post.. like/comments
        this.postUpdated = (updatedPost) => {
            this.items.forEach((post, i) => {
                if (updatedPost.id == post.id) {
                    this.items[i] = updatedPost
                }
            });
        }

        // attach hooks on event change
        this.appEvents.subscribe('event:changed', this.eventChangeHandler);
        this.appEvents.subscribe('marketplace:refresh', this.refreshHandler);
        this.appEvents.subscribe('marketplace:keywords:changed', this.keywordsChangeHandler);
        this.appEvents.subscribe('marketplace:post:udpated', this.postUpdated);

        // subscribe to current user
        this.userService.getCurrentUser().pipe(
            takeUntil(this.ngUnsubscribe),
            debounceTime(0)
        ).subscribe((user) => {
            if (user.id) {
                this.user = user;

                // NOTE[jg]  will we use it??
                // if (!this.categoryId) {
                //     this.categoryId = sessionStorage.getItem('marketplaceFilter') ? parseInt(sessionStorage.getItem('marketplaceFilter')) : 0;
                // }

                // cancel previous subscription
                if (this.subscriptionEvent) {
                    this.subscriptionEvent.unsubscribe();
                }

                this.subscriptionEvent = this.eventService.getCurrentEvent().pipe(
                    takeUntil(this.ngUnsubscribe),
                    debounceTime(0)
                ).subscribe(
                    (event) => {
                        this.event = event;
                        // reset offset of list
                        this.itemsOffset = 0;
                        // check if still in same event
                        if (this.user && this.user.selected_participant
                            && this.user.selected_participant.event_id == this.event.id
                            && this.event.marketplaces.length > 0
                        ) {
                            // NOTE[jg] not so good solution how to update participant...
                            this.user.selected_participant.event = this.event;
                            this.applyFilter(this.categoryId);
                        }
                    });

            } else {
                this.user = null;
                this.event = null;
                // cancel previous subscription
                if (this.subscriptionEvent) {
                    this.subscriptionEvent.unsubscribe();
                }
            }
        });
    }

    /**
     * load more marketplace posts in infinitive scroll
     *
     * @param event
     *
     * @return void
     */
    public loadData(event) {
        this.refresh(event, false, true);
    }

    /**
     * init list
     */
    public refresh($event?, reset: boolean = true, lock: boolean = false) {
        if (this.plt.eventInChange) {
            return;
        }

        if (!this.loading && !this.plt.disableBackgroundRefresh) {
            // avoid parallel loading, not it is noto possible
            // because we have keyword filter which is treggering
            // data load very offen
            if (lock) {
                this.loading = true;
            }

            if (reset) {
                this.itemsOffset = 0;
            }

            if (this.event) {
                this.marketplaceService.getPosts(
                    this.marketplace.id,
                    this.itemsLimit,
                    this.itemsOffset,
                    this.categoryId,
                    this.filterKeywords,
                    this.search
                ).subscribe((response) => {
                    if (reset) {
                        this.items = [];
                    }

                    response['items'].forEach(item => {
                        this.items.push(new MarketplacePost(item, this.userLang));
                    });
                    this.itemsTotal = response['total'];

                    // update offset by real count of items
                    this.itemsOffset = this.items.length;

                    // set preferences in keywords only for first time
                    // default value is null...
                    if (this.filterKeywords == null) {
                        this.filterKeywords = response['keywords'];
                        // if some keywords are selected, then open collabsible boxes
                        // with those keywords
                        if (this.filterKeywords && this.filterKeywords.length > 0 && !this.plt.sizeSm) {
                            this.keywords.pivots = this.filterKeywords;
                            this.keywords.checkCollabsible();
                        }
                    }

                    setTimeout(() => {
                        this.loading = false;
                        this.dataLoading = false;
                        // hide loading
                        if ($event) {
                            $event.target.complete();
                        }
                    }, lock ? 250 : 0);
                    // for case if lock is enabled, wait some time to ignore other ionChange events
                },
                    (error) => {
                        console.info(error);
                        // hide loading
                        if ($event) {
                            $event.target.complete();
                        } else {
                            this.loading = false;
                            this.dataLoading = false;
                        }
                    });
            }
        }
    }

    /**
     * open modal with marketplacePost add form
     * @return void
     */
    public async createMarketplacePost() {

        this.routerExtService.softNavigate();

        const modal = await this.modalController.create({
            component: MarketplaceAddComponent,
            cssClass: 'newsfeed-form',
            componentProps: {
                modal: true,
                event: this.event,
                displayBigTextarea: true,
                marketplace: this.marketplace,
                defaultLocale: this.event.default_locale
            }
        });

        modal.onWillDismiss().then((data) => {

            if (data.data) {
                this.refresh();
            }

            if (data.data?.action == 'close' || data.role == 'backdrop') {
                this.routerExtService.softBack();
            }

        });

        return await modal.present();
    }

    /**
     * aply the selected keywrods
     *
     * @param keywrods - selected keywords values
     *
     */
    public applyKeywords(keywords: any[], refresh: boolean = true) {
        this.filterKeywords = keywords;
        if (refresh) {
            this.refresh();
        }
    }

    /**
     * aply the selected option
     * @param value - selected filter value
     */
    public applyFilter(value?: number, resetFilter: boolean = false, lock: boolean = false) {

        if (resetFilter) {
            // reset offset
            this.itemsOffset = 0;
            this.applyKeywords(null, false);
            if (!this.plt.sizeSm) {
                this.keywords.pivots = null;
            }
        }

        this.categoryId = value;

        this.refresh(null, true, lock);

        // NOTE[jg]  will we use it??
        // sessionStorage.setItem('marketplaceFilter', String(this.categoryId));
    }

    /**
     * open keyword select modal
     *
     * @return void
     */
    public async showKeywordsModal() {

        this.routerExtService.softNavigate();

        const modal = await this.modalController.create({
            component: MarketplaceKeywordsComponent,
            cssClass: 'newsfeed-form',
            componentProps: {
                collapsible: false,
                favoriteSaving: true,
                allowKeywordSuggest: false,
                marketplace: this.marketplace,
                pivots: this.filterKeywords
            }
        });

        modal.onWillDismiss().then((data) => {

            if (data.data?.keywords) {
                this.applyKeywords(data.data.keywords);
            }

            if (data.data?.action == 'close' || data.role == 'backdrop') {
                this.routerExtService.softBack();
            }

        });

        return await modal.present();
    }

    /**
     * open keyword select modal
     *
     * @return void
     */
    public async openInfo() {

        this.routerExtService.softNavigate();

        const modal = await this.modalController.create({
            component: MarketplaceInfoComponent,
            cssClass: 'newsfeed-form',
            componentProps: {
                marketplace: this.marketplace,
            }
        });

        modal.onWillDismiss().then((data) => {
            if (data.data?.action == 'close' || data.role == 'backdrop') {
                this.routerExtService.softBack();
            }
        });

        return await modal.present();
    }
    /**
     * remove deleted marketplace from post lists
     *
     * @param id
     *
     * @return void
     */
    public marketplaceDeleted(id) {
        // use output to refresh list
        this.items = this.items.filter((item) => { return item.id != id });
    }

    /**
     * on destroy
     *
     * @return void
     */
    ngOnDestroy() {
        if (this.eventChangeHandler) {
            this.appEvents.unsubscribe('event:changed', this.eventChangeHandler);
        }
        if (this.keywordsChangeHandler) {
            this.appEvents.unsubscribe('marketplace:keywords:changed', this.keywordsChangeHandler);
        }
        if (this.refreshHandler) {
            this.appEvents.unsubscribe('marketplace:refresh', this.refreshHandler);
        }

        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

}
